// @ts-nocheck
import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
  createPass,
  deletePass,
  // fetchPasses, // Deprecate or re-evaluate if truly needed globally after city changes
  updatePass,
  fetchCities, // Needed for city selection
  fetchCity, // Needed for currentCity based on selection
} from "../../../../../../redux/slices/adminSlice";
import { ImSpinner2 } from "react-icons/im";
import { ToastContainer, toast } from "react-toastify";
import { AiOutlinePlus } from "react-icons/ai";
import { FaTrashCan, FaXmark } from "react-icons/fa6";
import Modal from "react-modal";
import Select from "react-select"; // Assuming you are using react-select or similar for dropdowns

// Images (not directly used in this component, keeping for context)
// import empty from "../../../../../../assets/images/empty.png";

import AdminCitiesLayout from "../CitiesLayout";
import PriorityPassCategories from "./PriorityPassCategories"; // Import the presentational component

function AdminPriorityPassRates() {
  const { token, isLoading, passes, cities, currentCity } = useSelector(
    (store) => store.admin
  );
  const dispatch = useDispatch();

  // State for Add Pass Modal
  const [isCreatePassModalOpen, setIsCreatePassModalOpen] = useState(false);
  const [passName, setPassName] = useState("");
  const [price, setPrice] = useState("");
  // NEW STATE: For city selection in Create Pass modal
  const [newPassSelectedCity, setNewPassSelectedCity] = useState(null);

  // State for Update/Delete Pass Modal
  const [isPassDetailModalOpen, setIsPassDetailModalOpen] = useState(false);
  const [currentPass, setCurrentPass] = useState(null);
  const [passNameModified, setPassNameModified] = useState("");
  const [priceModified, setPriceModified] = useState("");
  // No new state for selected city here, as we'll use currentCity._id for update/delete

  // State for City Selection in the Categories tab (main tab)
  const [selectedCity, setSelectedCity] = useState(null); // This is the city selected in the main tab

  // --- Effects ---

  // Fetch all cities for the dropdowns
  useEffect(() => {
    dispatch(fetchCities({ token, type: "priority" }));
  }, [token, dispatch]); // Added dispatch to dependency array

  // Format cities data for Select component
  const [citiesDataForSelect, setCitiesDataForSelect] = useState([]);
  useEffect(() => {
    const updatedCityData = cities?.map((city) => ({
      value: city._id,
      label: city.cityName,
    }));
    setCitiesDataForSelect(updatedCityData || []);

    // Set the first city as selected by default for the main tab and create modal if list is not empty
    if (!selectedCity && updatedCityData?.length > 0) {
      setSelectedCity(updatedCityData[0]);
    }
    // Also set default for newPassSelectedCity (for create modal)
    if (!newPassSelectedCity && updatedCityData?.length > 0) {
      setNewPassSelectedCity(updatedCityData[0]);
    }
  }, [cities, selectedCity, newPassSelectedCity]); // Added newPassSelectedCity to dependencies

  // Fetch the current city's details (which should include passes if they are city-bound)
  useEffect(() => {
    if (selectedCity?.value) {
      dispatch(
        fetchCity({
          cityId: selectedCity.value,
          token: token,
          type: "priority",
        })
      );
    }
  }, [selectedCity, token, dispatch]); // Added dispatch to dependency array

  // Populate modification fields when a pass is selected for the details modal
  useEffect(() => {
    if (currentPass) {
      setPassNameModified(currentPass.name ?? "");
      setPriceModified(currentPass.price ?? "");
    }
  }, [currentPass]);

  // --- Handlers ---

  // Handles the creation of a new pass
  async function handleCreatePass(e) {
    e.preventDefault();
    if (!passName || !price || !newPassSelectedCity?.value) {
      // Check for newPassSelectedCity
      toast.error("Please fill in all fields including city selection.");
      return;
    }
    dispatch(
      createPass({
        token,
        name: passName,
        price: price,
        cityId: newPassSelectedCity.value, // Pass the selected city ID
      })
    ).then((result) => {
      if (createPass.fulfilled.match(result)) {
        toast.success("Pass created successfully!");
        setIsCreatePassModalOpen(false);
        setPassName(""); // Clear form
        setPrice("");
        // After successful creation, if the newly created pass's city is the currently selected one in the main tab,
        // you might want to re-fetch the current city to update the UI.
        // Or, rely on the Redux slice's extraReducers for `createPass.fulfilled` to update `currentCity`.
        // The backend now returns the updated city, so the reducer should handle it.
        // Make sure your reducer for createPass.fulfilled updates `state.currentCity` if `action.payload.city._id === state.selectedCity.value`
        // and also `state.cities` array.
      } else {
        toast.error(result.payload || "Failed to create pass.");
      }
    });
  }

  async function handleUpdatePass(e) {
    e.preventDefault();
    if (!priceModified || !passNameModified) {
      toast.error("Please fill in the missing fields");
      return;
    }
    if (!currentCity?._id) {
      // Ensure we have the city ID from the current context
      toast.error("Cannot update pass: City not selected or available.");
      return;
    }

    dispatch(
      updatePass({
        token,
        passId: currentPass?._id,
        name: passNameModified,
        price: priceModified,
        cityId: currentCity._id, // Pass the city ID of the currently selected city
      })
    ).then((result) => {
      if (updatePass.fulfilled.match(result)) {
        toast.success("Pass updated successfully!");
        setIsPassDetailModalOpen(false);
      } else {
        toast.error(result.payload || "Failed to update pass.");
      }
    });
  }

  async function handleDeletePass(passId) {
    if (!currentCity?._id) {
      // Ensure we have the city ID from the current context
      toast.error("Cannot delete pass: City not selected or available.");
      return;
    }

    dispatch(
      deletePass({
        token,
        passId: passId || currentPass?._id, // Use the passed ID or the current pass's ID
        cityId: currentCity._id, // Pass the city ID of the currently selected city
      })
    ).then((result) => {
      if (deletePass.fulfilled.match(result)) {
        toast.success("Pass deleted successfully!");
        setIsPassDetailModalOpen(false);
        setCurrentPass(null); // Clear current pass selection
      } else {
        toast.error(result.payload || "Failed to delete pass.");
      }
    });
  }

  return (
    <div className="mt-10">
      <ToastContainer />

      {/* Create Pass Modal */}
      <Modal
        isOpen={isCreatePassModalOpen}
        onRequestClose={() => setIsCreatePassModalOpen(false)}
        className="flex h-full min-h-screen justify-center items-center lg:px-24 px-7"
      >
        <div className="bg-white shadow-lg rounded-lg text-shuttlelaneBlack lg:w-2/4 w-full p-7 px-10">
          <div className="flex items-center justify-between">
            <div className="">
              <h4 className="font-semibold">Create Pass</h4>
              <small>Create a new pass</small>
            </div>
            <FaXmark
              size={20}
              onClick={() => setIsCreatePassModalOpen(false)}
              className="cursor-pointer"
            />
          </div>

          <form className="w-full mt-5" onSubmit={handleCreatePass}>
            <div className="flex flex-col gap-y-5 lg:items-center gap-x-4">
              {/* City Selection Dropdown for Create Pass */}
              <div className="w-full flex flex-col">
                <label htmlFor="selectCity" className="text-sm mb-1">
                  Select City
                </label>
                <Select
                  options={citiesDataForSelect}
                  value={newPassSelectedCity}
                  onChange={setNewPassSelectedCity}
                  placeholder="Select a city..."
                  isSearchable={true}
                  isLoading={isLoading} // Show loading state if cities are being fetched
                  className="w-full text-sm"
                  styles={{
                    control: (base) => ({
                      ...base,
                      height: "44px", // Consistent height
                      minHeight: "44px",
                      borderColor:
                        "rgb(156 163 175 / var(--tw-border-opacity))", // Tailwind gray-400
                      boxShadow: "none",
                      "&:hover": {
                        borderColor:
                          "rgb(156 163 175 / var(--tw-border-opacity))",
                      },
                    }),
                    valueContainer: (base) => ({
                      ...base,
                      padding: "0 12px",
                    }),
                    singleValue: (base) => ({
                      ...base,
                      color: "#000", // Adjust text color if needed
                    }),
                    placeholder: (base) => ({
                      ...base,
                      color: "rgb(107 114 128 / var(--tw-text-opacity))", // Tailwind gray-500
                    }),
                  }}
                />
              </div>

              <div className="w-full flex flex-col">
                <label htmlFor="passName" className="text-sm">
                  Pass Type
                </label>
                <input
                  type="text"
                  placeholder="Standard Pass"
                  name="passName"
                  value={passName}
                  onChange={(e) => setPassName(e.target.value)}
                  className="w-full text-sm h-11 p-3 border-[0.3px] bg-transparent focus:outline-none border-gray-400 rounded-lg"
                />
              </div>

              <div className="w-full flex flex-col">
                <label htmlFor="price" className="text-sm">
                  Price
                </label>
                <div className="w-full flex items-center">
                  <label
                    htmlFor="price"
                    className="text-sm inline-block p-3 bg-gray-200 h-11 rounded-tl-lg rounded-bl-lg"
                  >
                    ₦
                  </label>
                  <input
                    type="number"
                    placeholder="40000" // Removed comma for number input placeholder
                    name="price"
                    value={price}
                    onChange={(e) => setPrice(e.target.value)}
                    className="w-full text-sm h-11 p-3 border-[0.3px] bg-transparent focus:outline-none border-gray-400 rounded-tr-lg rounded-br-lg"
                  />
                </div>
              </div>

              <button
                type="submit"
                disabled={isLoading}
                className="w-full flex justify-center items-center text-sm text-white hover:text-shuttlelaneBlack h-11 p-3 transition-all hover:border-[1px] hover:bg-transparent bg-shuttlelanePurple focus:outline-none border-gray-400 rounded-lg"
              >
                {isLoading ? (
                  <ImSpinner2 size={21} className="animate-spin" />
                ) : (
                  "Save"
                )}
              </button>
            </div>
          </form>
        </div>
      </Modal>

      {/* Pass Details Modal (Update/Delete Pass) */}
      <Modal
        isOpen={isPassDetailModalOpen}
        onRequestClose={() => setIsPassDetailModalOpen(false)}
        className="flex h-full min-h-screen justify-center items-center lg:px-24 px-7"
      >
        <div className="bg-white shadow-lg rounded-lg text-shuttlelaneBlack lg:w-2/4 w-full p-7 px-10">
          <div className="flex items-center justify-between">
            <div className="">
              <h4 className="font-semibold">{currentPass?.name}</h4>
              <small>More details for {currentPass?.name}</small>
              {/* Display the current city name here for clarity */}
              {currentCity?.cityName && (
                <p className="text-xs text-gray-500 mt-1">
                  In City: {currentCity.cityName}
                </p>
              )}
            </div>
            <FaXmark
              size={20}
              onClick={() => setIsPassDetailModalOpen(false)}
              className="cursor-pointer"
            />
          </div>

          <form className="w-full mt-5" onSubmit={handleUpdatePass}>
            <div className="flex flex-col gap-y-5 lg:items-center gap-x-4">
              <div className="w-full flex flex-col">
                <label htmlFor="passNameModified" className="text-sm">
                  Pass Type
                </label>
                <input
                  type="text"
                  placeholder="Standard Pass"
                  name="passNameModified"
                  value={passNameModified}
                  onChange={(e) => setPassNameModified(e.target.value)}
                  className="w-full text-sm h-11 p-3 border-[0.3px] bg-transparent focus:outline-none border-gray-400 rounded-lg"
                />
              </div>

              <div className="w-full flex flex-col">
                <label htmlFor="priceModified" className="text-sm">
                  Price
                </label>
                <div className="w-full flex items-center">
                  <label
                    htmlFor="priceModified"
                    className="text-sm inline-block p-3 bg-gray-200 h-11 rounded-tl-lg rounded-bl-lg"
                  >
                    ₦
                  </label>
                  <input
                    type="number"
                    placeholder="40000" // Removed comma for number input placeholder
                    name="priceModified"
                    value={priceModified}
                    onChange={(e) => setPriceModified(e.target.value)}
                    className="w-full text-sm h-11 p-3 border-[0.3px] bg-transparent focus:outline-none border-gray-400 rounded-tr-lg rounded-br-lg"
                  />
                </div>
              </div>

              <div className="w-full flex lg:flex-row flex-col lg:justify-between items-center gap-5">
                <button
                  type="submit"
                  disabled={isLoading}
                  className="w-full lg:w-[50%] flex justify-center items-center text-sm text-white hover:text-shuttlelaneBlack h-11 p-3 transition-all hover:border-[1px] hover:bg-transparent bg-shuttlelanePurple focus:outline-none border-gray-400 rounded-lg"
                >
                  {isLoading ? (
                    <ImSpinner2 size={21} className="animate-spin" />
                  ) : (
                    "Save Changes"
                  )}
                </button>
                <button
                  type="button" // Set to type="button" to prevent form submission
                  disabled={isLoading}
                  onClick={handleDeletePass} // Direct call
                  className="w-full lg:w-[50%] flex justify-center items-center text-sm text-white hover:text-shuttlelaneBlack h-11 p-3 transition-all hover:border-[1px] hover:bg-transparent bg-red-500 focus:outline-none border-gray-400 rounded-lg"
                >
                  {isLoading ? (
                    <ImSpinner2 size={21} className="animate-spin" />
                  ) : (
                    <div className="flex items-center gap-x-2">
                      <FaTrashCan size={16} />
                      <span className="text-sm">Delete</span>
                    </div>
                  )}
                </button>
              </div>
            </div>
          </form>
        </div>
      </Modal>

      <div className="flex gap-x-5 flex-wrap gap-y-3 items-end pb-3 border-b-[1.3px] lg:border-b-[.5px] border-b-gray-200">
        <div className="">
          <p className="text-lg font-semibold">Priority Pass Rates</p>
          <small className="">
            The rates per pass for{" "}
            <strong className="text-xs">Priority Pass</strong> bookings on
            Shuttlelane
          </small>
        </div>

        {/* Add Pass button */}
        <button
          onClick={() => {
            setIsCreatePassModalOpen(true);
            // Optionally, pre-select the main tab's selected city in the create modal
            setNewPassSelectedCity(selectedCity);
          }}
          className="w-auto border-dashed border-[.8px] lg:border-[.3px] border-shuttlelaneBlack p-1 rounded-sm flex items-center gap-x-1"
        >
          <AiOutlinePlus size={16} />
          <span className="text-xs">Create Pass</span>
        </button>
      </div>

      <AdminCitiesLayout
        type="priority"
        selectedCity={selectedCity} // Pass selectedCity down to AdminCitiesLayout for its own Select
        setSelectedCity={setSelectedCity} // Pass setter down
        citiesDataForSelect={citiesDataForSelect} // Pass formatted cities
        tab2Component={() => (
          <PriorityPassCategories
            selectedCity={selectedCity}
            setSelectedCity={setSelectedCity}
            citiesDataForSelect={citiesDataForSelect}
            isLoading={isLoading}
            currentCity={currentCity} // This is crucial for rendering passes of the selected city
            // passes={passes} // If passes are truly globally fetched, pass this. Otherwise, rely on currentCity.passes
            setIsPassDetailModalOpen={setIsPassDetailModalOpen}
            setCurrentPass={setCurrentPass}
            handleDeletePass={handleDeletePass} // Not needed here, as the actual deletion happens in the modal
          />
        )}
      />
    </div>
  );
}

export default AdminPriorityPassRates;
